import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import * as parser from 'components/ordering/acl/acl.js'
import {
  Basket,
  BasketContact,
  BasketHasNoContact,
  BasketItem,
  BasketList,
  BasketNameTooLong,
  BasketOverview,
  BasketParty,
  BasketUser,
  ConfiguredDeliveryPolicy,
  CreateAdditionalBasketFailure,
  CreateAdditionalBasketResponse,
  CreateAdditionalBasketUnionResponse,
  GetDeliveryPoliciesResponse,
  NameBasketFailure,
  NameBasketResponse,
  NameBasketUnionResponse,
  NoBasketSelected,
  NoNameGiven,
  PossibleTransferBasketRecipients,
  TransferBasketRecipient,
  TrivialDeliveryPolicy,
} from 'components/ordering/acl/internal-types.js'
import { AddBasketContactRequest, GetBasketContactRequest } from './basket-contact.js'
import { CreateAdditionalBasketRequest } from './create-additional-basket.js'
import { GetDeliveryPoliciesPayload, SetBasketDeliveryPolicyRequest } from './delivery-policy.js'
import { GetBasketListPayload } from './get-basket-list.js'
import { GetPossibleTransferBasketRecipientsRequest } from './get-possible-transfer-basket-recipients.js'
import { NameBasketRequest } from './name-basket.js'
import { RemoveBasketRequest } from './remove-basket.js'
import { SelectBasketRequest } from './select-basket.js'
import { TransferSelectedBasketRequest } from './transfer-selected-basket.js'
import { GetBasketOverviewRequest } from './get-basket-overview.js'
import { executeOrderingRequest } from 'components/ordering/executor.js'
import {
  BasketDTO,
  ConfiguredDeliveryPolicyDTO,
  GetBaskets,
  GetDeliveryPolicies,
  NoBasketSelectedDTO,
  NoNameGivenDTO,
  TrivialDeliveryPolicyDTO,
} from '@cloudsuite/cs-ordering-defs'


export interface BasketApi {
  // think we should apply interface segregation on this and move the individual functions to their respective ts files

  // basket-overview-store
  getBasketList(request: GetBasketListPayload): Promise<BasketList>
  getBasketOverview(request: GetBasketOverviewRequest): Promise<BasketOverview>

  // delivery-policy-store
  getDeliveryPolicies(request: GetDeliveryPoliciesPayload): Promise<GetDeliveryPoliciesResponse>

  // non store requests
  createAdditionalBasket(request: CreateAdditionalBasketRequest): Promise<CreateAdditionalBasketUnionResponse>
  nameBasket(request: NameBasketRequest): Promise<NameBasketUnionResponse>
  selectBasket(request: SelectBasketRequest): Promise<void>
  setBasketDeliveryPolicy(request: SetBasketDeliveryPolicyRequest): Promise<void>
  transferSelectedBasket(request: TransferSelectedBasketRequest): Promise<void>
  getPossibleTransferBasketRecipients(request: GetPossibleTransferBasketRecipientsRequest): Promise<PossibleTransferBasketRecipients>
  getBasketContact(request: GetBasketContactRequest): Promise<BasketContact | BasketHasNoContact>
  addBasketContact(request: AddBasketContactRequest): Promise<void>
  removeBasket(request: RemoveBasketRequest): Promise<void>
}


export const BASKET_API = Symbol('BASKET_API')


@registerDependency(FAKE, BASKET_API)
export class FakeBasketApi implements BasketApi {
  requests: { endpointUrl: URL, data?: unknown }[] = []

  private baskets: [Basket, Basket] = [
    new Basket(
      'selected-basket-id',
      'basket name',
      1,
      2,
      new ConfiguredDeliveryPolicy(1, 'jaarorder'),
    ),
    new Basket(
      'another-basket-id',
      new NoNameGiven(),
      17,
      18,
      new TrivialDeliveryPolicy(),
    ),
  ]

  basketListResponse: BasketList = new BasketList(this.baskets, this.baskets[0])
  get selectedBasketId(): string | NoBasketSelected {
    const selectedBasket = this.basketListResponse.selectedBasket
    if (selectedBasket instanceof NoBasketSelected) return selectedBasket
    return selectedBasket.basketId
  }

  basketOverviewResponse: BasketOverview = new BasketOverview([
    new BasketItem(
      '1',
      'basket name',
      new TrivialDeliveryPolicy(),
      new BasketUser(
        'Henry',
        'de',
        'Derde',
        'hdederde@henry.com',
      ),
      new BasketParty(
        23,
        'Henry\'s Party',
      ),
    ),
    new BasketItem(
      '2',
      'basket 2',
      new ConfiguredDeliveryPolicy(1, 'jaarorder'),
      new BasketUser(
        'Henry',
        '',
        'Twee',
        'htweed@henry.com',
      ),
      new BasketParty(
        23,
        'Henry\'s Party',
      ),
    )
  ])

  maxBasketNameLength: number = 200
  createAdditionalBasketReturnedId: string = 'additional basket id'
  deliveryPoliciesResponse = new GetDeliveryPoliciesResponse(
    [
      new TrivialDeliveryPolicy(),
      new ConfiguredDeliveryPolicy(1, 'jaarorder'),
    ],
    new TrivialDeliveryPolicy(),
  )

  getPossibleTransferBasketRecipientsResponse = new PossibleTransferBasketRecipients(
    [
      new TransferBasketRecipient(
        6,
        'Willie Wortel',
        'wwortel@duckstad.nl',
      ),
      new TransferBasketRecipient(
        7,
        'Donald Duck',
        'dduck@duckstad.nl',
      ),
    ],
  )

  basketContact: BasketContact | BasketHasNoContact = new BasketHasNoContact()

  async getDeliveryPolicies(request: GetDeliveryPoliciesPayload): Promise<GetDeliveryPoliciesResponse> {
    const endpointUrl = new URL('/endpoint/GetDeliveryPolicies/', window.location.href)
    request
    this.requests.push({ endpointUrl: endpointUrl })
    return this.deliveryPoliciesResponse
  }

  async setBasketDeliveryPolicy(request: SetBasketDeliveryPolicyRequest): Promise<void> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
  }

  async transferSelectedBasket(request: TransferSelectedBasketRequest): Promise<void> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
  }

  // @ts-expect-error TS6133: request is declared but its value is never read.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getBasketList(request: GetBasketListPayload): Promise<BasketList> {
    const endpointUrl = new URL('/endpoint/GetBaskets/', window.location.href)
    this.requests.push({ endpointUrl })
    return this.basketListResponse
  }

  async getBasketOverview(request: GetBasketOverviewRequest): Promise<BasketOverview> {
    this.requests.push({ endpointUrl: request.endpointUrl })
    return this.basketOverviewResponse
  }

  async createAdditionalBasket(request: CreateAdditionalBasketRequest): Promise<CreateAdditionalBasketUnionResponse> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
    if (request.data.name.length > this.maxBasketNameLength) {
      return new CreateAdditionalBasketFailure(
        new BasketNameTooLong(
          this.maxBasketNameLength,
          request.data.name.length,
        ),
      )
    }
    return new CreateAdditionalBasketResponse(this.createAdditionalBasketReturnedId)
  }

  async nameBasket(request: NameBasketRequest): Promise<NameBasketUnionResponse> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
    if (request.data.name.length > this.maxBasketNameLength) {
      return new NameBasketFailure(
        new BasketNameTooLong(
          this.maxBasketNameLength,
          request.data.name.length,
        ),
      )
    }
    return new NameBasketResponse()
  }

  async selectBasket(request: SelectBasketRequest): Promise<void> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
  }

  async getPossibleTransferBasketRecipients(request: GetPossibleTransferBasketRecipientsRequest): Promise<PossibleTransferBasketRecipients> {
    this.requests.push({ endpointUrl: request.endpointUrl })
    return this.getPossibleTransferBasketRecipientsResponse
  }

  async addBasketContact(request: AddBasketContactRequest): Promise<void> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
  }

  async removeBasket(request: RemoveBasketRequest) {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
  }

  async getBasketContact(request: GetBasketContactRequest): Promise<BasketContact | BasketHasNoContact> {
    this.requests.push({ endpointUrl: request.endpointUrl, data: request.data })
    return this.basketContact
  }
}


type BasketRequest = {
  endpointUrl: URL
  method: 'POST' | 'GET'
  headers?: HeadersInit
  data?: any
}

const doFetch = (request: BasketRequest): Promise<Response> => {
  const { endpointUrl, data, ...init } = request

  if (data !== undefined) {
    Object.assign(init, { body: JSON.stringify(data) })
  }

  return fetch(endpointUrl, init)
}


@registerDependency(REAL, BASKET_API)
export class RealBasketApi implements BasketApi {
  async getDeliveryPolicies(request: GetDeliveryPoliciesPayload): Promise<GetDeliveryPoliciesResponse> {
    const response = await executeOrderingRequest(new GetDeliveryPolicies(
      request.shop_id,
      request.party_id
    ))

    function parseDeliveryPolicy(policy: TrivialDeliveryPolicyDTO | ConfiguredDeliveryPolicyDTO): TrivialDeliveryPolicy | ConfiguredDeliveryPolicy {
      if (policy instanceof TrivialDeliveryPolicyDTO) {
        return new TrivialDeliveryPolicy()
      }
      return new ConfiguredDeliveryPolicy(
        policy.id,
        policy.name
      )
    }
    return new GetDeliveryPoliciesResponse(
      response.delivery_policies.map(parseDeliveryPolicy),
      parseDeliveryPolicy(response.default_),
    )
  }

  async getBasketList(request: GetBasketListPayload): Promise<BasketList> {
    const mapDeliveryPolicy = (dto: TrivialDeliveryPolicyDTO | ConfiguredDeliveryPolicyDTO): TrivialDeliveryPolicy | ConfiguredDeliveryPolicy => {
      if (dto instanceof TrivialDeliveryPolicyDTO) return new TrivialDeliveryPolicy()
      return new ConfiguredDeliveryPolicy(dto.id, dto.name)
    }

    const mapBasketName = (dto: string | NoNameGivenDTO): string | NoNameGiven => {
      if (dto instanceof NoNameGivenDTO) return new NoNameGiven()
      return dto
    }

    const mapBasket = (dto: BasketDTO): Basket => new Basket(
      dto.basket_id,
      mapBasketName(dto.name),
      dto.line_count,
      dto.cumulated_count,
      mapDeliveryPolicy(dto.delivery_policy),
    )

    const mapSelectedBasket = (dto: NoBasketSelectedDTO | BasketDTO): NoBasketSelected | Basket => {
      if (dto instanceof NoBasketSelectedDTO) return new NoBasketSelected()
      return mapBasket(dto)
    }

    const query = new GetBaskets(request.userId, request.partyId, request.shopId)
    const { baskets, selected_basket } = await executeOrderingRequest(query)

    return new BasketList(
      baskets.map(mapBasket),
      mapSelectedBasket(selected_basket)
    )
  }

  async getBasketOverview(request: GetBasketOverviewRequest): Promise<BasketOverview> {
    const response = await doFetch(request)
    return parser.parseGetBasketOverviewForUserResponse(await response.json())
  }

  async createAdditionalBasket(request: CreateAdditionalBasketRequest): Promise<CreateAdditionalBasketUnionResponse> {
    const response = await doFetch(request)
    return parser.parseCreateAdditionalBasketUnionResponse(await response.json())
  }

  async nameBasket(request: NameBasketRequest): Promise<NameBasketUnionResponse> {
    const response = await doFetch(request)
    return parser.parseNameBasketUnionResponse(await response.json())
  }

  async getPossibleTransferBasketRecipients(request: GetPossibleTransferBasketRecipientsRequest): Promise<PossibleTransferBasketRecipients> {
    const response = await doFetch(request)
    return parser.parsePossibleTransferBasketRecipients(await response.json())
  }

  async getBasketContact(request: GetBasketContactRequest): Promise<BasketContact | BasketHasNoContact> {
    const response = await doFetch(request)
    return parser.parseBasketContactResponse(await response.json())
  }

  async addBasketContact(request: AddBasketContactRequest): Promise<void> {
    await doFetch(request)
  }

  async selectBasket(request: SelectBasketRequest): Promise<void> {
    await doFetch(request)
  }

  async setBasketDeliveryPolicy(request: SetBasketDeliveryPolicyRequest): Promise<void> {
    await doFetch(request)
  }

  async transferSelectedBasket(request: TransferSelectedBasketRequest): Promise<void> {
    await doFetch(request)
  }

  async removeBasket(request: RemoveBasketRequest): Promise<void> {
    await doFetch(request)
  }
}
